import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  User, 
  MapPin, 
  Calendar, 
  Users, 
  DollarSign,
  Loader,
  CheckCircle,
  Copy,
  ExternalLink,
  ArrowRight,
  Mail,
  ChevronDown,
  Check,
  Castle,
  Ship,
  Globe,
  Sparkles
} from 'lucide-react';
import { toast } from '../../../utils/toast';

const ConvertLeadWithTripModal = ({ isOpen, onClose, lead, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Form, 2: Success
  const [loading, setLoading] = useState(false);
  const [createTrip, setCreateTrip] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [showDestinationDropdown, setShowDestinationDropdown] = useState(false);
  
  // Form state - Pre-fill from lead data
  const [tripName, setTripName] = useState('');
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [adults, setAdults] = useState('');
  const [children, setChildren] = useState('');
  
  // Update form when lead changes
  React.useEffect(() => {
    if (lead) {
      setTripName(`${lead.firstName} ${lead.lastName}'s ${getDestinationName(lead.destination)} Trip`);
      setDestination(lead.destination || 'disney_world');
      setStartDate(lead.departureDate || '');
      setEndDate(lead.returnDate || '');
      setAdults(lead.adults || 2);
      setChildren(lead.children || 0);
    }
  }, [lead]);
  
  // Success data
  const [successData, setSuccessData] = useState(null);

  const destinations = [
    { value: 'disney_world', label: 'Walt Disney World', icon: Sparkles, color: 'text-gray-700' },
    { value: 'disneyland', label: 'Disneyland', icon: Castle, color: 'text-gray-700' },
    { value: 'universal_orlando', label: 'Universal Orlando', icon: Globe, color: 'text-gray-700' },
    { value: 'disney_cruise', label: 'Disney Cruise Line', icon: Ship, color: 'text-gray-700' }
  ];

  function getDestinationName(dest) {
    const names = {
      disney_world: 'Disney World',
      disneyland: 'Disneyland',
      universal_orlando: 'Universal Orlando',
      disney_cruise: 'Disney Cruise'
    };
    return names[dest] || 'Disney';
  }

  const getSelectedDestination = () => {
    return destinations.find(d => d.value === destination) || destinations[0];
  };

  const handleConvert = async () => {
    setLoading(true);
    try {
      const endpoint = createTrip 
        ? `${import.meta.env.VITE_API_URL}/api/crm/leads/${lead.leadId}/convert-with-trip`
        : `${import.meta.env.VITE_API_URL}/api/crm/leads/${lead.leadId}/convert`;

      const body = createTrip ? {
        tripName,
        destination,
        startDate: startDate || null,
        endDate: endDate || null,
        adults: parseInt(adults),
        children: parseInt(children),
        sendQuestionnaireEmail: sendEmail
      } : {};

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to convert lead');
      }

      const result = await response.json();
      
      setSuccessData({
        clientId: result.data.clientId,
        clientName: `${lead.firstName} ${lead.lastName}`,
        tripId: result.data.tripId,
        tripName: result.data.tripName,
        questionnaireLink: result.data.questionnaireLink,
        emailSent: sendEmail
      });
      
      setStep(2);
      
      if (onSuccess) {
        onSuccess(result.data);
      }
    } catch (error) {
      console.error('Conversion error:', error);
      toast.error(error.message || 'Failed to convert lead');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleClose = () => {
    setStep(1);
    setSuccessData(null);
    onClose();
  };

  if (!isOpen || !lead) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            {step === 1 ? (
              // Step 1: Conversion Form
              <>
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Convert Lead</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {lead.firstName} {lead.lastName}
                    </p>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Create Trip Toggle */}
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createTrip}
                        onChange={(e) => setCreateTrip(e.target.checked)}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <span className="text-sm font-semibold text-gray-900">
                          Create trip immediately
                        </span>
                        <p className="text-xs text-gray-600 mt-0.5">
                          Recommended: Creates client + trip + questionnaire link in one step
                        </p>
                      </div>
                    </label>
                  </div>

                  {createTrip && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-4"
                    >
                      {/* Trip Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Trip Name *
                        </label>
                        <input
                          type="text"
                          value={tripName}
                          onChange={(e) => setTripName(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., Smith Family Disney Trip"
                        />
                      </div>

                      {/* Destination - Custom Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Destination *
                        </label>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowDestinationDropdown(!showDestinationDropdown)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between hover:border-gray-400 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              {React.createElement(getSelectedDestination().icon, { 
                                className: `w-5 h-5 ${getSelectedDestination().color}` 
                              })}
                              <span className="text-gray-900">{getSelectedDestination().label}</span>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showDestinationDropdown ? 'rotate-180' : ''}`} />
                          </button>
                          
                          <AnimatePresence>
                            {showDestinationDropdown && (
                              <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden"
                              >
                                {destinations.map((dest) => {
                                  const DestIcon = dest.icon;
                                  return (
                                    <button
                                      key={dest.value}
                                      type="button"
                                      onClick={() => {
                                        setDestination(dest.value);
                                        setShowDestinationDropdown(false);
                                        // Update trip name when destination changes
                                        if (lead) {
                                          setTripName(`${lead.firstName} ${lead.lastName}'s ${dest.label} Trip`);
                                        }
                                      }}
                                      className={`w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                                        destination === dest.value ? 'bg-blue-50' : ''
                                      }`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <DestIcon className={`w-5 h-5 ${dest.color}`} />
                                        <span className="text-sm font-medium text-gray-900">{dest.label}</span>
                                      </div>
                                      {destination === dest.value && (
                                        <Check className="w-5 h-5 text-blue-600" />
                                      )}
                                    </button>
                                  );
                                })}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>

                      {/* Dates */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      {/* Travelers */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Adults
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={adults}
                            onChange={(e) => setAdults(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Children
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={children}
                            onChange={(e) => setChildren(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      {/* Send Email Option */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            checked={sendEmail}
                            onChange={(e) => setSendEmail(e.target.checked)}
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-900">
                              Send questionnaire email now
                            </span>
                            <p className="text-xs text-gray-600 mt-0.5">
                              Automatically email the questionnaire link to the client
                            </p>
                          </div>
                        </label>
                      </div>
                    </motion.div>
                  )}
                </div>

                <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <div className="flex gap-3">
                    {createTrip && (
                      <button
                        onClick={() => setCreateTrip(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Convert Only
                      </button>
                    )}
                    <button
                      onClick={handleConvert}
                      disabled={loading || (createTrip && !tripName.trim())}
                      className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      {loading ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Converting...
                        </>
                      ) : (
                        <>
                          {createTrip ? 'Convert & Create Trip' : 'Convert to Client'}
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              // Step 2: Success Screen
              <>
                <div className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                  
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    Success!
                  </h2>
                  <p className="text-lg text-gray-600 mb-8">
                    Lead successfully converted to client{createTrip && ' and trip created'}!
                  </p>

                  {/* Client Info */}
                  <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Client Information</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Client Name:</span>
                        <span className="text-sm font-medium text-gray-900">{successData?.clientName}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Client ID:</span>
                        <code className="text-xs font-mono bg-white px-2 py-1 rounded border border-gray-200">
                          {successData?.clientId}
                        </code>
                      </div>
                    </div>
                  </div>

                  {createTrip && successData?.tripId && (
                    <>
                      {/* Trip Info */}
                      <div className="bg-blue-50 rounded-lg p-6 mb-6 text-left border-2 border-blue-200">
                        <h3 className="text-sm font-semibold text-blue-900 mb-4">Trip Information</h3>
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-blue-700">Trip Name:</span>
                            <span className="text-sm font-medium text-blue-900">{successData?.tripName}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-blue-700">Trip ID:</span>
                            <code className="text-xs font-mono bg-white px-2 py-1 rounded border border-blue-300">
                              {successData?.tripId}
                            </code>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-blue-700">Status:</span>
                            <span className="inline-flex px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full border border-yellow-200">
                              Pending Questionnaire
                            </span>
                          </div>
                        </div>

                        {/* Questionnaire Link */}
                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                          <label className="block text-xs font-semibold text-blue-900 mb-2">
                            Questionnaire Link
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={successData?.questionnaireLink}
                              readOnly
                              className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-lg font-mono"
                            />
                            <button
                              onClick={() => copyToClipboard(successData?.questionnaireLink)}
                              className="px-3 py-2 text-blue-600 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                              title="Copy link"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                          {successData?.emailSent && (
                            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              Email sent to client
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Next Steps */}
                      <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Next Steps</h3>
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li className="flex items-start gap-2">
                            <span className="text-blue-500 mt-0.5">1.</span>
                            <span>Client will receive questionnaire link via email{!successData?.emailSent && ' (or you can send it manually)'}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-blue-500 mt-0.5">2.</span>
                            <span>Once completed, trip status will change to "Planning"</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-blue-500 mt-0.5">3.</span>
                            <span>Itinerary will be auto-generated and trip status will become "Confirmed"</span>
                          </li>
                        </ul>
                      </div>
                    </>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => window.location.href = `/crm/clients`}
                      className="w-full px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <User className="w-4 h-4" />
                      View Client in CRM
                      <ExternalLink className="w-4 h-4" />
                    </button>
                    
                    {createTrip && (
                      <button
                        onClick={() => window.location.href = `/crm/trips`}
                        className="w-full px-4 py-3 text-sm font-semibold text-blue-600 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                      >
                        <MapPin className="w-4 h-4" />
                        View Trip in CRM
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    )}
                    
                    <button
                      onClick={handleClose}
                      className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ConvertLeadWithTripModal;

